import { c as _c } from "react/compiler-runtime";
import type { LivePlayParams } from '@module/apis/client-side/live/type';
import SettingConfig from '@module/config/setting';
import { syncLoginInfoQueryData } from '@module/helpers/auth/syncLoginInfoQueryData';
import useGetLoginInfoQuery from '@module/helpers/auth/useGetLoginInfoQuery';
import { aspCond } from '@module/helpers/conditions/condition';
import { useModalAdapterOptional } from '@module/helpers/modal/adapter';
import { resolveThumbnailRatioAspect } from '@module/helpers/play/resolveThumbnailRatioAspect';
import { useFetchLivePlayMutation } from '@module/queries/live/liveQueryHooks';
import useChatPanelStore from '@module/stores/useChatPanelStore';
import { useFetchLivePlayMutationStore } from '@module/stores/useFetchLivePlayMutationStore';
import type { EssentialMediaInfo, GoPlayParams } from '@module/types/play/goPlayTypes';
import { BaseHttpResponseError } from '@module/utils/error/apiErrors';
import { ContextualError } from '@module/utils/error/context-error';
import { useQueryClient } from '@tanstack/react-query';
import { useParams, usePathname, useRouter } from 'next/navigation';
import { useEffect, useRef } from 'react';
import { checkAdultPopup, checkForPaidContent, doesUserHaveSiteModeAccess, isPasswordProtectedStream, validatePasswordFormat } from './useGoPlay.helpers';
import useLivePlayErrorHandler from './useLivePlayErrorHandler';
type ExtendedMutationOptions = Parameters<typeof useFetchLivePlayMutation>[0] & {
  beforeOnError?: (err: Error, vars: ReturnType<typeof useFetchLivePlayMutation>['variables']) => Error | undefined;
  beforeOnBaseHTTPResponseError?: (err: BaseHttpResponseError, vars: ReturnType<typeof useFetchLivePlayMutation>['variables']) => BaseHttpResponseError | undefined;
};
interface UsePlayEntryParams {
  mutationOptions?: ExtendedMutationOptions;
}
function useGoPlay(t0) {
  const $ = _c(44);
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? {} : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    mutationOptions
  } = t1;
  const router = useRouter();
  const queryClient = useQueryClient();
  const modalAdapter = useModalAdapterOptional();
  const {
    loginInfo
  } = useGetLoginInfoQuery();
  const pathname = usePathname();
  const {
    userId: pathnameUserId
  } = useParams();
  const {
    handleLivePlayError
  } = useLivePlayErrorHandler();
  const mediaRef = useRef(null);
  let t2;
  if ($[2] !== pathname) {
    t2 = pathname.includes("/play/");
    $[2] = pathname;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const isPlayPage = t2;
  let t3;
  if ($[4] !== pathname) {
    t3 = pathname.includes("/setting/broadcast") || pathname.includes("/broadcast/setting");
    $[4] = pathname;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const isBroadcastSettingPage = t3;
  const {
    setData,
    setVariables,
    setError,
    data,
    setStatus
  } = useFetchLivePlayMutationStore();
  let t4;
  if ($[6] !== modalAdapter) {
    t4 = () => {
      modalAdapter?.clear();
    };
    $[6] = modalAdapter;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const clearModals = t4;
  let t5;
  if ($[8] !== isBroadcastSettingPage || $[9] !== isPlayPage || $[10] !== pathname || $[11] !== queryClient || $[12] !== router) {
    t5 = (data_0, vars) => {
      if (data_0.loginInfo) {
        syncLoginInfoQueryData(queryClient, data_0.loginInfo);
      }
      if (isBroadcastSettingPage) {
        return;
      }
      if (isPlayPage && vars.userId && pathname.includes(vars.userId)) {
        return;
      }
      let playLink = `/play/${vars.userId}`;
      if (aspCond.if("iframe-asp")) {
        playLink = `/asp/play/${vars.userId}`;
      }
      if (playLink) {
        router.push(playLink);
        if (aspCond.if("iframe-asp")) {
          SettingConfig.aspUtils?.scrollTop();
        }
      } else {
        throw new ContextualError("Invalid play link", {
          userId: vars.userId,
          pathname
        });
      }
    };
    $[8] = isBroadcastSettingPage;
    $[9] = isPlayPage;
    $[10] = pathname;
    $[11] = queryClient;
    $[12] = router;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  const mutation = useFetchLivePlayMutation({
    onSuccess: t5,
    onError: (err, vars_0) => {
      let resolvedError;
      if (err instanceof BaseHttpResponseError && mutationOptions?.beforeOnBaseHTTPResponseError) {
        resolvedError = mutationOptions?.beforeOnBaseHTTPResponseError?.(err, vars_0);
      } else {
        if (err instanceof Error && mutationOptions?.beforeOnError) {
          resolvedError = mutationOptions?.beforeOnError?.(err, vars_0);
        } else {
          resolvedError = err;
        }
      }
      if (resolvedError instanceof BaseHttpResponseError) {
        handleLivePlayError({
          userId: vars_0.userId || "",
          err: resolvedError,
          fetchLivePlayParams: vars_0,
          mutateAsync: mutation.mutateAsync,
          media: mediaRef.current
        });
      }
    },
    onSettled(playInfoOrUndefined, error, variables) {
      if (playInfoOrUndefined) {
        const playInfo = playInfoOrUndefined;
        setData(playInfo);
        const aspect = resolveThumbnailRatioAspect(playInfo.thumbnailRatio);
        if (aspect) {
          useChatPanelStore.getState().setVideoAspect(aspect);
        }
      }
      setError(error);
      setVariables(variables);
    },
    onMutate: setVariables
  });
  let t6;
  if ($[14] !== mutation.status || $[15] !== setStatus) {
    t6 = () => {
      setStatus(mutation.status);
    };
    $[14] = mutation.status;
    $[15] = setStatus;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  let t7;
  if ($[17] !== mutation.status) {
    t7 = [mutation.status];
    $[17] = mutation.status;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  useEffect(t6, t7);
  let goPlay;
  if ($[19] !== clearModals || $[20] !== isBroadcastSettingPage || $[21] !== isPlayPage || $[22] !== loginInfo || $[23] !== modalAdapter || $[24] !== mutation || $[25] !== router) {
    goPlay = async (media, mutateAsyncParams) => {
      const onAuthConfirm = () => {
        clearModals();
        modalAdapter?.showIdentityVerification();
      };
      if (!doesUserHaveSiteModeAccess(loginInfo, media.isAdult, onAuthConfirm, modalAdapter ?? undefined)) {
        return;
      }
      if (!isPlayPage && !checkForPaidContent(media.type, loginInfo, modalAdapter ?? undefined)) {
        return;
      }
      if (await isPasswordProtectedStream(media, loginInfo, goPlay, modalAdapter ?? undefined)) {
        return;
      }
      if (!aspCond.if("iframe-asp") && !checkAdultPopup(media, modalAdapter ?? undefined)) {
        return;
      }
      if (!media.isLive && !isBroadcastSettingPage && media.userId) {
        router.push(`/channel/${media.userId}/notice`);
        return;
      }
      if (media.pw && !validatePasswordFormat(media.pw, modalAdapter ?? undefined)) {
        return;
      }
      const params = {
        userId: decodeURIComponent(media.userId),
        action: "watch",
        ...mutateAsyncParams
      };
      if (media.isPw && media.pw) {
        params.password = String(media.pw);
      }
      mediaRef.current = media;
      return mutation.mutateAsync(params);
    };
    $[19] = clearModals;
    $[20] = isBroadcastSettingPage;
    $[21] = isPlayPage;
    $[22] = loginInfo;
    $[23] = modalAdapter;
    $[24] = mutation;
    $[25] = router;
    $[26] = goPlay;
  } else {
    goPlay = $[26];
  }
  let t8;
  if ($[27] !== data?.chatServer || $[28] !== data?.media || $[29] !== mutation) {
    t8 = async () => {
      const params_0 = {
        action: "retry",
        token: data?.chatServer.token,
        t: data?.chatServer.t,
        userId: data?.media.userId
      };
      return mutation.mutateAsync(params_0);
    };
    $[27] = data?.chatServer;
    $[28] = data?.media;
    $[29] = mutation;
    $[30] = t8;
  } else {
    t8 = $[30];
  }
  const retryLivePlay = t8;
  let t9;
  if ($[31] !== isPlayPage || $[32] !== mutation || $[33] !== pathnameUserId) {
    t9 = async () => {
      if (!isPlayPage) {
        return;
      }
      if (!pathnameUserId || Array.isArray(pathnameUserId)) {
        return;
      }
      const params_1 = {
        action: "watch",
        userId: decodeURIComponent(pathnameUserId)
      };
      return mutation.mutateAsync(params_1);
    };
    $[31] = isPlayPage;
    $[32] = mutation;
    $[33] = pathnameUserId;
    $[34] = t9;
  } else {
    t9 = $[34];
  }
  const fetchPlayInfoWithinPlayPage = t9;
  let t10;
  if ($[35] !== data || $[36] !== goPlay || $[37] !== isPlayPage) {
    t10 = () => {
      if (!isPlayPage || !data) {
        return;
      }
      goPlay(data.media);
    };
    $[35] = data;
    $[36] = goPlay;
    $[37] = isPlayPage;
    $[38] = t10;
  } else {
    t10 = $[38];
  }
  const refetchPlayInfoIfPlayPage = t10;
  let t11;
  if ($[39] !== fetchPlayInfoWithinPlayPage || $[40] !== goPlay || $[41] !== refetchPlayInfoIfPlayPage || $[42] !== retryLivePlay) {
    t11 = {
      goPlay,
      retryLivePlay,
      fetchPlayInfoWithinPlayPage,
      refetchPlayInfoIfPlayPage
    };
    $[39] = fetchPlayInfoWithinPlayPage;
    $[40] = goPlay;
    $[41] = refetchPlayInfoIfPlayPage;
    $[42] = retryLivePlay;
    $[43] = t11;
  } else {
    t11 = $[43];
  }
  return t11;
}
export default useGoPlay;